import { Checkbox, FormControlLabel, FormLabel, Grid, TextField } from "@mui/material";
import { defineMessage, FormattedMessage, MessageDescriptor } from "react-intl";
import { ErrorText } from "../ErrorText";
import { DateTimeField } from "../DateTimeField";
import { Fragment, ReactNode, CSSProperties } from "react";
import { FieldErrorList } from "../../types";
import { MeetingLengthField } from "./widgets";
import { SaveMeetingInput } from "@/api";
import DescriptionEditor from "../DescriptionEditor";

type MeetingFormProps = {
    getFieldErrors: (fieldName: keyof SaveMeetingInput) => FieldErrorList;
    updateFormData: (partialData: Partial<SaveMeetingInput>) => void;
    disabled?: boolean;
    formData: SaveMeetingInput;
    meetingStartsNow: boolean;
    setMeetingStartsNow: (v: boolean) => void;
};

type RowProps = {
    label: MessageDescriptor;
    children: ReactNode;
    required?: boolean;
    contentStyle?: CSSProperties;
};

const Row = ({ label, children, required, contentStyle }: RowProps) => (
    <Fragment>
        <Grid size={{ md: 2, sm: 3, xs: 12 }} style={{ display: "flex", paddingTop: "1.2em" }}>
            <FormLabel required={required}>
                <FormattedMessage {...label} />
            </FormLabel>
        </Grid>
        <Grid size={{ md: 10, sm: 9, xs: 12 }} style={{ display: "flex", alignItems: "center", ...contentStyle }}>
            {children}
        </Grid>
    </Fragment>
);

const MeetingForm = (props: MeetingFormProps) => {
    const { getFieldErrors, updateFormData, disabled, formData, meetingStartsNow, setMeetingStartsNow } = props;

    return (
        <Grid container spacing={2}>
            <Row
                label={defineMessage({ id: "summary", defaultMessage: "Summary" })}
                required
                contentStyle={{ flexDirection: "column", alignItems: "flex-start" }}
            >
                <TextField
                    id="summary"
                    type="text"
                    fullWidth
                    size="small"
                    error={getFieldErrors("summary").length > 0}
                    disabled={disabled}
                    value={formData.summary}
                    onChange={(e) => updateFormData({ summary: e.target.value })}
                    required
                />
                <ErrorText data={getFieldErrors("summary")} />
            </Row>
            <Row label={defineMessage({ id: "time.start", defaultMessage: "Start time" })} required>
                <DateTimeField
                    disabled={disabled || meetingStartsNow}
                    value={formData.startTime}
                    errors={getFieldErrors("startTime")}
                    setValue={(v) =>
                        updateFormData({
                            startTime: v,
                        })
                    }
                    required
                />
                <FormControlLabel
                    style={{ marginLeft: "0.5em" }}
                    control={
                        <Checkbox
                            disabled={disabled}
                            color="primary"
                            checked={meetingStartsNow}
                            onClick={(_) => setMeetingStartsNow(!meetingStartsNow)}
                        />
                    }
                    label={<FormattedMessage id="meeting.startsNow" defaultMessage="Immediately" />}
                />
            </Row>
            <Row label={defineMessage({ id: "meetingLength.title", defaultMessage: "Length" })} required>
                <MeetingLengthField {...props} />
            </Row>
            <Row label={defineMessage({ id: "pinCode.label", defaultMessage: "PIN code" })}>
                <TextField
                    id="pin_code"
                    type="text"
                    size="small"
                    error={getFieldErrors("pinCode").length > 0}
                    disabled={disabled}
                    value={formData.pinCode ?? ""}
                    onChange={(e) => updateFormData({ pinCode: e.target.value })}
                />
                <ErrorText data={getFieldErrors("pinCode")} />
            </Row>
            <Row
                label={defineMessage({
                    id: "allowUnmuteAll",
                    defaultMessage: "Allow for the participants to unmute themselves",
                })}
            >
                <Checkbox
                    checked={formData.allowUnmute}
                    color="primary"
                    onChange={(event) => updateFormData({ allowUnmute: Boolean(event.target.checked) })}
                />
            </Row>
            <Row label={defineMessage({ id: "description", defaultMessage: "Description" })}>
                <div style={{ width: "100%" }}>
                    <DescriptionEditor
                        initialData={formData.description}
                        onChange={(data) => updateFormData({ description: data })}
                    />
                    <ErrorText data={getFieldErrors("description")} />
                </div>
            </Row>
        </Grid>
    );
};

export default MeetingForm;
